const AUTH_KEYS = [
  'authToken',
  'refreshToken',
  'tokenType',
  'userid',
  'userEmail',
  'userRole',
  'profileCompleted',
];

export const clearAuthData = (): void => {
  AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const getUserRoles = (): string[] => {
  const raw = localStorage.getItem('userRole');
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch {
    return raw ? [raw] : [];
  }
};

export const hasRole = (role: string): boolean => getUserRoles().includes(role);

interface FetchWithRetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  timeoutMs?: number;
  onRetry?: (attempt: number, delayMs: number) => void;
}

interface FetchWithRetryResult {
  response: Response;
  retried: boolean;
}

// Serialises concurrent refresh attempts so only one refresh call is in-flight at a time.
let refreshPromise: Promise<string | null> | null = null;

const attemptTokenRefresh = (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    try {
      const res = await fetch('https://api.homecookt.com/api/v1/users/refresh-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (res.ok) {
        const data = await res.json();
        const newToken: string = data.access_token;
        localStorage.setItem('authToken', newToken);
        return newToken;
      }

      // Refresh token itself is expired or invalid — force logout
      clearAuthData();
      window.dispatchEvent(new Event('sessionExpired'));
      return null;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Drop-in replacement for fetch() that transparently refreshes the access token on 401.
 * Reads the current authToken from localStorage and injects the Authorization header.
 * Dispatches a 'sessionExpired' window event and clears auth data if the refresh fails.
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const makeHeaders = (token: string): HeadersInit => ({
    ...((options.headers as Record<string, string>) || {}),
    Authorization: `Bearer ${token}`,
  });

  const token = localStorage.getItem('authToken') || '';
  const firstResponse = await fetch(url, { ...options, headers: makeHeaders(token) });

  if (firstResponse.status !== 401) return firstResponse;

  // Access token expired — try to refresh
  const newToken = await attemptTokenRefresh();
  if (!newToken) return firstResponse; // caller will see the 401

  return fetch(url, { ...options, headers: makeHeaders(newToken) });
};

export const getFirstImageUrl = (imageUrls: string | string[] | null | undefined): string => {
  const fallback = 'https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg';
  if (!imageUrls) return fallback;
  const url = Array.isArray(imageUrls) ? imageUrls[0] : imageUrls;
  if (!url || url === 'string') return fallback;
  return url;
};

export const fetchWithRetry = async (
  url: string,
  requestOptions: RequestInit,
  retryOptions: FetchWithRetryOptions = {}
): Promise<FetchWithRetryResult> => {
  const {
    maxRetries = 3,
    initialDelay = 2000,
    timeoutMs = 15000,
    onRetry,
  } = retryOptions;

  let lastResponse: Response | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...requestOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.status === 429 && attempt < maxRetries) {
        lastResponse = response;
        const retryAfterHeader = response.headers.get('Retry-After');
        let delay: number;

        if (retryAfterHeader) {
          delay = parseInt(retryAfterHeader, 10) * 1000;
          if (isNaN(delay)) {
            delay = initialDelay * Math.pow(2, attempt);
          }
        } else {
          delay = initialDelay * Math.pow(2, attempt);
        }

        if (onRetry) {
          onRetry(attempt + 1, delay);
        }

        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      return { response, retried: attempt > 0 };
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw new Error('REQUEST_TIMEOUT');
      }

      throw error;
    }
  }

  if (lastResponse) {
    return { response: lastResponse, retried: true };
  }

  throw new Error('REQUEST_FAILED');
};
